// The angles a picture can be of, and the only ones worth naming.
//
// One list, two uses — the angle a Phase 2 image is generated FROM in the 3D pipeline, and
// the direction Phase 1 classifies each listing photo as. They have to be the same
// vocabulary or neither composes: a photo classified "back" is only useful because it can
// be asked for by that name.
//
// It was drawn up as exactly what a multi-view reconstructor accepted as inputs — a front
// view plus seven named angles — and there was a third use, the slots on its bench page.
// The slots went with that engine. The vocabulary stayed: what a photograph is OF is a fact about the photograph, and it was
// never the reconstructor's to own.
//
// LEFT AND RIGHT ARE THE VIEWER'S — the left of a product is the side you see on your left
// when you stand in front of it and look at its front. One convention for the two words
// across the whole app: it is the frame the six face names are fixed against (faces.ts,
// where 'left' is −x), so the photograph and the mesh face now mean the same side. They did
// not: this said the OBJECT's own left, which is the opposite one, and a picture filed
// 'left' was of the face the box calls 'right'. See _GALLERY_VIEW_PROMPT in notice.py.
//
// Ids and labels only. The wording sent to a model lives in notice.py (_VIEW_PROMPTS and
// _GALLERY_VIEW_PROMPT): the same sentence has to be careful about handedness in a way
// that only matters where the call is made, and a second copy here was a second place to
// get it wrong.
export const VIEW_DIRECTIONS = [
  { id: 'front', label: 'Front' },
  { id: 'back', label: 'Back' },
  { id: 'left', label: 'Left' },
  { id: 'right', label: 'Right' },
  { id: 'top', label: 'Top' },
  { id: 'bottom', label: 'Bottom' },
  { id: 'leftFront', label: 'Left front 45°' },
  { id: 'rightFront', label: 'Right front 45°' },
] as const;

export type ViewDirection = (typeof VIEW_DIRECTIONS)[number]['id'];

// THE ANGLES WORTH ASKING FOR when generating a part's photograph. One, now.
//
// The list above is what a picture can BE of, and it is still the whole of it: Phase 1
// classifies a listing's photographs across all eight, and every one of them can be picked
// as a reconstructor's input. This is the narrower question — which angles are worth
// spending a generation on — and it used to have the same answer only because a multi-view
// reconstructor wanted one picture per slot.
//
// `front` is the only angle anything downstream reads (frontOf in ThreeDStep, which picks the part's default input),
// so the other seven were being generated, labelled, and never looked at again. Widening
// this back out is one line if that changes.
export const GENERATED_VIEWS = VIEW_DIRECTIONS.filter((v) => v.id === 'front');

// What a picture is when it is of no nameable angle: an in-situ shot, a dimension
// drawing, a detail crop, two chairs at a table. Its own value rather than a blank,
// because "nothing classified this yet" and "this was looked at and belongs to no
// direction" are different facts, and only the second one is an answer.
export const OTHER_DIRECTION = 'other';

export type Direction = ViewDirection | typeof OTHER_DIRECTION;

export const DIRECTION_IDS: string[] = [...VIEW_DIRECTIONS.map((v) => v.id), OTHER_DIRECTION];

export const directionLabel = (id: string): string =>
  VIEW_DIRECTIONS.find((v) => v.id === id)?.label ?? 'Other';

// Where a picture sorts in a strip shown by direction: the vocabulary's own order, which
// already opens with front and back — the two views a reconstructor wants before any
// other, and the two whose absence it has to invent around — and closes with 'other'.
//
// A picture with no direction yet sorts with 'other', just after it. Neither is a view
// anything can be told to use, and a strip nobody has classified keeps the merchant's
// order untouched because every rank ties (the sort is stable).
export const directionRank = (id: string | undefined): number => {
  const i = DIRECTION_IDS.indexOf(id ?? '');
  return i < 0 ? DIRECTION_IDS.length : i;
};

// WHICH OF A PART'S THREE DIMENSIONS EACH EDGE OF ITS BOX SHOWS, on a square-on photograph.
//
// x, y and z are the part's width, depth and height AS IT SITS IN THE FINISHED PRODUCT —
// that is the estimate call's own definition (_ESTIMATE_FIELDS in notice.py) — and the
// photograph is of the finished product. So the part's orientation in the picture is the
// product's, and nothing has to be derived from the mask: the direction the picture was
// taken from is the whole of the mapping.
//
// TWO OF THE THREE, never all of them. The third axis points at the camera and has no edge
// on screen to sit on; a number written on one of the other two would be a number about the
// wrong dimension.
//
// SQUARE-ON ONLY. At 45° both horizontal extents project into the box's width, so what the
// bottom edge spans is neither the width nor the depth — and 'other' is a picture nobody
// could name a side for at all. Both answer null, and nothing is drawn.
export type BoxAxes = { across: 'x' | 'y'; up: 'z' };

export const viewAxes = (view: string | undefined): BoxAxes | null => {
  if (view === 'front' || view === 'back') return { across: 'x', up: 'z' };
  if (view === 'left' || view === 'right') return { across: 'y', up: 'z' };
  return null;
};
